<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { Head, Link, usePage } from '@inertiajs/vue3'
import { homeHrefFor, type Role } from '../navigation'
import TopBar from '../components/nav/TopBar.vue'
import BottomTabBar from '../components/nav/BottomTabBar.vue'
import NotificationBell from '../components/NotificationBell.vue'
import ThemeToggle from '../components/ThemeToggle.vue'
import InstallPrompt from '../components/InstallPrompt.vue'
import PushPrompt from '../components/PushPrompt.vue'
import { useOwnerInboxStore } from '../stores/ownerInbox'
import type { OwnerInboxCounts } from '../types/owner-inbox'
import { overlayShown } from '../composables/useBannerSpace'
import { promptBannersAllowed, pwaVisible } from '../lib/pwa'

// inheritAttrs: false — иначе пропсы страницы утекут fallthrough-атрибутами на корневой div (см. Фаза 1).
defineOptions({ inheritAttrs: false })
const page = usePage()

// Логотип отдаётся статикой из public/; :src-binding обходит статичный asset-transform Vue (см. Task 8).
const logoSrc = '/header-logo.png'

// Тот же приём, что в TopBar.vue: логотип не может вести в закрытый раздел
// (у иногороднего /schedule отдаёт редирект).
const user = computed(() => page.props.current_user as { role: Role } | undefined)
const coworking = computed(() => page.props.coworking_available !== false)
const doorOpen = computed(() => page.props.door_access_open !== false)
const homeHref = computed(() =>
  user.value ? homeHrefFor(user.value.role, coworking.value, doorOpen.value) : '/profile',
)

// Раскатка PWA (см. lib/pwa.ts): при закрытом флаге установку предлагаем только владельцу.
const pwaOn = computed(() => pwaVisible(page.props))

// Право на нижние полосы: их видит только человек с оплаченным пакетом или бронью
// (владелец и админ — всегда), считает сервер. Новичку сразу после регистрации две
// полосы поверх первого экрана — просьба ни о чём: включать уведомления ему пока
// нечего, а приложение он ставит, когда сюда возвращается. Гейт стоит здесь, а не
// внутри компонентов, намеренно: он про ОДНИ ЛИШЬ полосы, а те же компоненты
// плашкой и поповером спрашивают в потоке страницы и под гейт не попадают.
// ...и молчат целиком, пока открыт слой поверх страницы (выпадашка колокольчика):
// полоса видна прямо под ним, и две одинаковые кнопки «Включить уведомления» на
// одном экране — просьба, задвоенная своей же соседкой.
const bannersOn = computed(() => promptBannersAllowed(page.props) && !overlayShown.value)

// Нижняя полоса на экране одна. Кто её занял — знает только сам PushPrompt:
// его состояние приезжает асинхронно (pushState() — промис).
const pushBannerShown = ref(false)

// Живые счётчики очередей: проп owner_inbox есть только у владельца. Он же
// сидит стору первым значением (на каждую навигацию Inertia приезжает свежий),
// а подписка и опрос включаются лишь там, где проп вообще пришёл.
const inboxStore = useOwnerInboxStore()
const ownerInbox = computed(() => page.props.owner_inbox as OwnerInboxCounts | undefined)
watch(ownerInbox, (value) => inboxStore.seed(value), { immediate: true })
onMounted(() => {
  if (ownerInbox.value) inboxStore.start()
})
onUnmounted(() => inboxStore.stop())
</script>

<template>
  <div class="flex min-h-screen flex-col bg-bg text-text">
    <!-- Заголовок вкладки по умолчанию. Без него Inertia оставляет document.title пустым
         на каждой странице без своего <Head>. Свой <Head> страницы его перекрывает: из
         нескольких title Inertia берёт последний, а провайдер страницы создаётся позже. -->
    <Head title="Наша Душа" />
    <template v-if="page.props.current_user">
      <!-- Порог навигации — lg (1024px), а НЕ md: телефон в ландшафте даёт около
           870–915 CSS-px, то есть проходил порог md и получал десктопную шапку.
           Персональных пунктов у клиента и владельца восемь плюс «Управление ▾»,
           в такую ширину они не влезают и наезжают на колокольчик с аватаром.
           На lg завязана вся обвязка «есть ли таб-бар»: мобильная полоса и
           нижний отступ main здесь, BottomTabBar, MoreSheet, BANNER_SLOT_CLASS
           в useBannerSpace.ts и своя высота main в pages/Door.vue. Меняешь порог —
           меняй везде разом, иначе полоса баннера уедет под таб-бар. -->
      <TopBar class="hidden lg:flex" />
      <!-- Мобильная верхняя полоса: на мобайле TopBar скрыт, а у персонала нет вкладки
           «Уведомления» — колокольчик здесь даёт доступ к оповещениям (спека §5.4).
           Два NotificationBell (тут и в TopBar) безопасны: store.subscribe() идемпотентен
           (первым делом unsubscribe, единственная подписка) → максимум одна ActionCable-подписка. -->
      <header class="flex items-center justify-between border-b border-border bg-surface px-4 h-12 lg:hidden">
        <Link :href="homeHref" class="flex items-center" aria-label="Наша Душа">
          <img :src="logoSrc" alt="Наша Душа" class="h-6 w-auto" />
        </Link>
        <div class="flex items-center gap-1">
          <ThemeToggle />
          <NotificationBell />
        </div>
      </header>
      <!-- Нижний отступ учитывает не только таб-бар, но и нижнюю полосу (установка или
           уведомления — на экране всегда одна, см. арбитраж ниже): она fixed, свою
           высоту кладёт в --bottom-prompt-space (см. useBannerSpace.ts), иначе
           накрывает конец страницы. -->
      <main
        class="mx-auto w-full max-w-6xl flex-1 px-4 py-6 pb-[calc(6rem+var(--bottom-prompt-space,0px))] lg:pb-[calc(1.5rem+var(--bottom-prompt-space,0px))]"
      >
        <!-- Задача 19: контроллеры давно кладут flash (redirect_to ..., notice: "…"),
             например "Анкета удалена" или "Анкета одобрена и опубликована", но до сих
             пор нигде в приложении не отображались, кроме локального чтения на SignIn.vue.
             Общий баннер здесь закрывает разрыв сразу для всех разделов, а не только
             для модерации каталога; стилистика та же, что уже была на SignIn.vue
             (bg-good-soft/text-crit), без нового паттерна. Flash одноразовый (Rails его
             читает и стирает на первом же рендере), поэтому баннер сам исчезает при
             следующем переходе без ручного таймера/крестика.

             Баннер возникает после действия владельца, вне потока чтения. Зрячий
             заметит его сам, а скринридер без live-региона не озвучит вовсе.
             Поэтому notice получает role="status": подтверждение вроде «Анкета
             удалена» ждёт паузы в речи и не рвёт то, что читается сейчас.
             aria-live="polite" продублирован явно, хотя роль его и подразумевает:
             Safari/VoiceOver исторически озвучивают status надёжнее с явным
             атрибутом. У alert стоит role="alert", потому что неудачу («Не удалось
             …») владелец должен услышать сразу, а не после текущей фразы;
             подразумеваемое ролью assertive оставлено как есть, aria-live не дублируем.

             Задача 8 (хвост ревью): контейнеры держим в разметке ВСЕГДА (v-if
             заменён на класс-переключатель), меняется только текст внутри.
             Живой регион, вставленный в DOM ОДНОВРЕМЕННО с текстом (как было
             раньше через v-if), скринридер зачастую ещё не успевает
             зарегистрировать — первое объявление тонет в тишине. Постоянно
             смонтированный узел эту гонку убирает: к моменту, когда текст
             меняется, регион уже известен ассистивной технологии.

             Important 3 (финальное ревью): переключатель ошибочно использовал
             `hidden` (display: none). Узел с display: none исключён из дерева
             доступности целиком — предыдущий абзац переставал быть правдой:
             живой регион, которого AT не видит, не объявляет ничего, когда
             текст появляется. Так же ведёт себя `visibility: hidden` — оба
             непригодны для «скрыт визуально, но виден AT». `sr-only` скрывает
             узел только зрячим (абсолютное позиционирование + обрезка 1px),
             сам узел и его текст остаются в дереве доступности, поэтому
             появление текста долетает до скринридера. -->
        <p data-testid="flash-notice" role="status" aria-live="polite" class="mb-4 rounded-lg bg-good-soft px-3 py-2.5 text-sm text-text" :class="{ 'sr-only': !page.flash?.notice }">
          {{ page.flash?.notice }}
        </p>
        <p data-testid="flash-alert" role="alert" class="mb-4 rounded-lg bg-crit-soft px-3 py-2.5 text-sm text-crit" :class="{ 'sr-only': !page.flash?.alert }">
          {{ page.flash?.alert }}
        </p>
        <slot />
      </main>
      <BottomTabBar />
      <!-- Полоса пуша идёт первой: уведомления дают функцию, установка — удобство,
           и на Android с десктопом одно от другого не зависит. На iOS предложение
           пуша молчит до установки (pushState → ios_install_required), поэтому там
           порядок сам собой оказывается обратным. -->
      <PushPrompt v-if="bannersOn" variant="banner" @update:visible="pushBannerShown = $event" />
      <InstallPrompt v-if="pwaOn && bannersOn" variant="banner" :suppressed="pushBannerShown" />
    </template>
    <!-- Аноним (вход/регистрация): минимальный экран без навигации и футера.
         Юр-документы доступны с футера лендинга, из чекбокса согласия на регистрации
         (ConsentCheckboxes) и по прямым URL (/offer, /policy, …). -->
    <div v-else class="flex flex-1 flex-col">
      <main class="flex flex-1 items-center justify-center px-4 py-8">
        <slot />
      </main>
    </div>
  </div>
</template>
